/**
 * "Meu trabalho" — lista cross-projeto das fases onde sou responsavel.
 *
 * Ponto de entrada principal pra quem so quer despachar o proprio bolso de
 * tarefas sem navegar projeto-a-projeto. Backend ja ordena por urgencia.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertOctagon, AlertTriangle, Clock, Ban, Play, Briefcase, ClipboardList, CheckCircle2 } from 'lucide-react';
import { meAPI } from '../../services/inovacaoAPI';
import { mensagemErro } from './labels';

const URGENCIA_VISUAL = {
  atrasada:    { cor: '#e74c3c', label: 'Atrasada',     Icon: AlertOctagon },
  em_risco:    { cor: '#e67e22', label: 'Em risco',     Icon: AlertTriangle },
  bloqueada:   { cor: '#c0392b', label: 'Bloqueada',    Icon: Ban },
  em_execucao: { cor: '#3498db', label: 'Em andamento', Icon: Play },
  planejada:   { cor: '#95a5a6', label: 'Planejada',    Icon: Clock },
};

export default function MeuTrabalho() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [questionarios, setQuestionarios] = useState([]);

  useEffect(() => {
    meAPI.fasesPendentes()
      .then(setItems)
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao carregar minhas fases')))
      .finally(() => setLoading(false));
    // Questionários de esforço onde sou o operador responsável. Sem papel no
    // módulo — respondente comum também vê. Falha silenciosa: é uma seção extra.
    meAPI.analisesParaResponder()
      .then(setQuestionarios)
      .catch(() => {});
  }, []);

  // Agrupa por urgencia preservando a ordem ja vinda do backend
  const grupos = {};
  for (const it of items) {
    if (!grupos[it.urgencia]) grupos[it.urgencia] = [];
    grupos[it.urgencia].push(it);
  }

  const ordemGrupos = ['atrasada', 'em_risco', 'bloqueada', 'em_execucao', 'planejada'];

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={28} /> Meu trabalho
        </h1>
        <p style={{ margin: '6px 0 0', color: '#7f8c8d' }}>
          Fases pendentes sob sua responsabilidade, de todos os projetos, ordenadas por urgência.
        </p>
      </div>

      {questionarios.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: '0.95rem', color: 'var(--brand, #20a490)', margin: '0 0 10px',
                       display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase',
                       letterSpacing: 0.5 }}>
            <ClipboardList size={16} /> Questionários para responder
            <span style={{ color: '#95a5a6' }}>({questionarios.length})</span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
            {questionarios.map((q) => <CardQuestionario key={q.id} q={q} />)}
          </div>
        </div>
      )}

      {loading && <p style={{ color: '#bdc3c7' }}>Carregando…</p>}

      {!loading && items.length === 0 && questionarios.length === 0 && (
        <div style={{ background: '#fff', padding: 40, borderRadius: 12, textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <p style={{ color: '#27ae60', fontSize: '1.1rem', margin: 0 }}>
            ✅ Nada pendente. Voce esta em dia.
          </p>
          <p style={{ color: '#95a5a6', fontSize: '0.9rem', marginTop: 8 }}>
            Quando uma fase onde voce eh responsavel mudar de estado pra ativo, aparece aqui.
          </p>
        </div>
      )}

      {ordemGrupos.map((urg) => {
        const lista = grupos[urg];
        if (!lista || !lista.length) return null;
        const cfg = URGENCIA_VISUAL[urg];
        const { Icon } = cfg;
        return (
          <div key={urg} style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: '0.95rem', color: cfg.cor, margin: '0 0 10px',
                         display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase',
                         letterSpacing: 0.5 }}>
              <Icon size={16} /> {cfg.label} <span style={{ color: '#95a5a6' }}>({lista.length})</span>
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 12 }}>
              {lista.map((it) => <CardFase key={it.fase_id} item={it} cor={cfg.cor} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CardQuestionario({ q }) {
  const enviado = q.estado === 'em_revisao_gni';
  let statusLabel, statusCor, StatusIcon;
  if (enviado) {
    statusLabel = 'Enviado, em análise do gestor';
    statusCor = '#27ae60';
    StatusIcon = CheckCircle2;
  } else if (q.respondida) {
    statusLabel = 'Rascunho salvo: continuar';
    statusCor = '#f39c12';
    StatusIcon = ClipboardList;
  } else {
    statusLabel = 'Responder questionário';
    statusCor = 'var(--brand, #20a490)';
    StatusIcon = ClipboardList;
  }
  return (
    <Link
      to={`/inovacao/analises/${q.id}`}
      style={{
        textDecoration: 'none', color: 'inherit', background: '#fff',
        padding: 14, borderRadius: 10, borderLeft: `4px solid ${statusCor}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'block',
        transition: 'transform 0.12s, box-shadow 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}
    >
      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#2c3e50', marginBottom: 6 }}>
        {q.titulo}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: statusCor, fontWeight: 500 }}>
        <StatusIcon size={15} /> {statusLabel}
      </div>
    </Link>
  );
}

function CardFase({ item, cor }) {
  const horasEst = parseFloat(item.horas_estimadas || 0);
  const horasCons = parseFloat(item.horas_consumidas || 0);
  const pct = horasEst > 0 ? Math.min(100, Math.round((horasCons / horasEst) * 100)) : 0;
  return (
    <Link
      to={`/inovacao/projetos/${item.projeto.id}`}
      style={{
        textDecoration: 'none', color: 'inherit', background: '#fff',
        padding: 14, borderRadius: 10, borderLeft: `4px solid ${cor}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'block',
        transition: 'transform 0.12s, box-shadow 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
      }}
    >
      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#2c3e50', marginBottom: 2 }}>
        {item.eh_escopo_adicional && (
          <span style={{ background: '#ff7a00', color: '#fff', padding: '1px 6px',
                         borderRadius: 4, fontSize: '0.65rem', marginRight: 6 }}>+ ESCOPO</span>
        )}
        {item.fase_nome}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#7f8c8d', marginBottom: 8 }}>
        {item.projeto.nome}
        {item.fase_categoria && <span> · {item.fase_categoria}</span>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
        <span style={{ color: '#7f8c8d' }}>
          {horasCons}h / {horasEst}h ({pct}%)
        </span>
        <span style={{ color: item.urgencia === 'atrasada' ? '#e74c3c' : '#7f8c8d' }}>
          {item.data_fim_planejada ? `fim ${formatarDataBR(item.data_fim_planejada)}` : 'sem prazo'}
        </span>
      </div>
      {horasEst > 0 && (
        <div style={{ height: 4, background: '#ecf0f1', borderRadius: 2, marginTop: 6 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: cor }} />
        </div>
      )}
    </Link>
  );
}

function formatarDataBR(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}
